import { generateKsuid, generateDocumentId } from "@/utils/ksuidGenerator";
import { CNS_API_BASE } from "@/config";
import { parseProblemDetail, throwOnProblemResponse, type ApiErrorLike } from "@/types/problem-detail";
import { isAllowedChatFileType, resolveChatFileMimeType } from "@/constants/allowedFileTypes";

const DOCUMENTS_URL = `${CNS_API_BASE}/documents`;

export type DocumentOrigin = "upload" | "internal" | "mcp" | "connector";

export interface Document {
  id: string;
  documentName: string;
  documentSize: number;
  documentType: string;
  indexingStatus: "PENDING" | "INDEXED" | "FAILED";
  indexingError?: string;
  origin?: DocumentOrigin;
  sourceConnectionPublicId?: string;
  externalRef?: Record<string, unknown>;
  lastSyncedAt?: string;
  createdAt: string;
  lineageEdges: LineageEdge[];
}

export type DocumentIndexingStatus = Document["indexingStatus"];

export interface LineageEdge {
  id: string; // Required - generated by frontend using KSUID
  documentId: string; // Required - the document ID (subject when subject_type=document)
  entityType: string;
  entityPublicId: string;
  createdAt?: string; // Optional - generated by backend, returned in response
}

export interface DocumentUploadResponse {
  id: string;
  documentName: string;
  documentSize: number;
  documentType: string;
  indexingStatus: DocumentIndexingStatus;
  indexingError?: string;
  createdAt: string;
  lineageEdges: LineageEdge[];
}

export interface DocumentDownloadResponse {
  downloadUrl: string;
}

export interface PresignedUploadResponse {
  uploadUrl: string;
  s3Key: string;
  documentId: string;
}

export class DocumentService {
  private buildDocumentUploadFormData(
    projectId: string,
    file: File,
    documentId: string,
    lineageEdges?: LineageEdge[],
  ): FormData {
    const formData = new FormData();
    formData.append("file", file, file.name);

    const metadata = {
      id: documentId,
      projectId,
      documentName: file.name,
      documentType: resolveChatFileMimeType(file) || file.type || "application/octet-stream",
      lineageEdges: lineageEdges || [],
    };

    const metadataBlob = new Blob([JSON.stringify(metadata)], {
      type: "application/json",
    });
    formData.append("metadata", metadataBlob, "metadata.json");
    return formData;
  }

  private uploadErrorFromXhr(xhr: XMLHttpRequest): Error {
    let message: string | undefined;
    try {
      const problem = JSON.parse(xhr.responseText) as {
        detail?: string;
        title?: string;
      };
      message = problem.detail ?? problem.title;
    } catch {
      // ignore parse errors
    }
    if (!message) {
      if (xhr.status === 413) {
        message = "File too large. Maximum size is 10MB.";
      } else if (xhr.status === 502 || xhr.status === 504) {
        message = "Upload timed out or server unavailable. Please try again.";
      } else {
        message = "Failed to upload document";
      }
    }
    return new Error(message);
  }

  /**
   * Upload a document to a project
   */
  async uploadDocument(
    projectId: string, 
    file: File, 
    documentId: string,
    lineageEdges?: LineageEdge[]
  ): Promise<DocumentUploadResponse> {
    const formData = this.buildDocumentUploadFormData(
      projectId,
      file,
      documentId,
      lineageEdges,
    );

    const response = await fetch(DOCUMENTS_URL, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const problem = await parseProblemDetail(response);
      let message = problem?.detail ?? problem?.title;
      if (!message) {
        if (response.status === 413) {
          message = 'File too large. Maximum size is 10MB.';
        } else if (response.status === 502 || response.status === 504) {
          message = 'Upload timed out or server unavailable. Please try again.';
        } else {
          message = 'Failed to upload document';
        }
      }
      const error = new Error(message) as ApiErrorLike;
      if (problem) {
        error.problemDetail = problem;
      }
      throw error;
    }

    return response.json();
  }

  /**
   * Upload with XMLHttpRequest so upload byte progress can be reported to the composer.
   */
  uploadDocumentWithProgress(
    projectId: string,
    file: File,
    documentId: string,
    lineageEdges: LineageEdge[] | undefined,
    onProgress: (progress: number) => void,
  ): Promise<DocumentUploadResponse> {
    const formData = this.buildDocumentUploadFormData(
      projectId,
      file,
      documentId,
      lineageEdges,
    );

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", DOCUMENTS_URL);
      xhr.withCredentials = true;

      xhr.upload.addEventListener("progress", (event) => {
        if (!event.lengthComputable || event.total <= 0) return;
        onProgress(Math.round((event.loaded / event.total) * 100));
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText) as DocumentUploadResponse);
          } catch {
            reject(new Error("Failed to parse upload response"));
          }
          return;
        }
        reject(this.uploadErrorFromXhr(xhr));
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Failed to upload document"));
      });

      xhr.addEventListener("abort", () => {
        reject(new Error("Upload cancelled"));
      });

      xhr.send(formData);
    });
  }

  /**
   * Get all documents for a project
   */
  async getProjectDocuments(projectId: string): Promise<Document[]> {
    const response = await fetch(`${CNS_API_BASE}/projects/${projectId}/documents`, {
      credentials: 'include',
    });

    if (!response.ok) {
      await throwOnProblemResponse(response, 'Failed to fetch project documents');
    }

    return response.json();
  }

  /**
   * Get all documents for a conversation (from its project)
   */
  async getConversationDocuments(conversationId: string): Promise<Document[]> {
    const response = await fetch(`${CNS_API_BASE}/conversations/${conversationId}/documents`, {
      credentials: 'include',
    });

    if (!response.ok) {
      await throwOnProblemResponse(response, 'Failed to fetch conversation documents');
    }

    return response.json();
  }

  /**
   * Get download URL for a document
   */
  async getDocumentDownloadUrl(
    projectId: string, 
    documentId: string, 
    expirationMinutes: number = 60
  ): Promise<string> {
    const response = await fetch(
      `${CNS_API_BASE}/projects/${projectId}/documents/${documentId}?expirationMinutes=${expirationMinutes}`,
      { credentials: 'include' }
    );

    if (!response.ok) {
      await throwOnProblemResponse(response, 'Failed to get download URL');
    }

    const data: DocumentDownloadResponse = await response.json();
    return data.downloadUrl;
  }

  /**
   * Delete a document
   */
  async deleteDocument(projectId: string, documentId: string): Promise<void> {
    const response = await fetch(
      `${CNS_API_BASE}/projects/${projectId}/documents/${documentId}`,
      {
        method: 'DELETE',
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await throwOnProblemResponse(response, 'Failed to delete document');
    }
  }

  /**
   * Get document count for a project
   */
  async getProjectDocumentCount(projectId: string): Promise<number> {
    const response = await fetch(`${CNS_API_BASE}/projects/${projectId}/documents/count`, {
      credentials: 'include',
    });

    if (!response.ok) {
      await throwOnProblemResponse(response, 'Failed to get document count');
    }

    const data = await response.json();
    return data.count;
  }

  /**
   * Generate a pre-signed upload URL for direct S3 upload
   */
  async generatePresignedUploadUrl(
    projectId: string,
    fileName: string,
    contentType: string,
    expirationMinutes: number = 30
  ): Promise<PresignedUploadResponse> {
    const response = await fetch(`${DOCUMENTS_URL}/presigned-upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        projectId,
        fileName,
        contentType,
        expirationMinutes,
      }),
    });

    if (!response.ok) {
      await throwOnProblemResponse(response, 'Failed to generate pre-signed upload URL');
    }

    return response.json();
  }

  /**
   * Confirm document upload after successful S3 upload
   */
  async confirmDocumentUpload(
    projectId: string,
    documentId: string,
    s3Key: string,
    fileName: string,
    fileSize: number,
    contentType: string,
    lineageEdges?: LineageEdge[]
  ): Promise<DocumentUploadResponse> {
    const response = await fetch(`${DOCUMENTS_URL}/confirm-upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        projectId,
        documentId,
        s3Key,
        fileName,
        fileSize,
        contentType,
        lineageEdges: lineageEdges || [],
      }),
    });

    if (!response.ok) {
      await throwOnProblemResponse(response, 'Failed to confirm document upload');
    }

    return response.json();
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get file icon based on document type (PDF and text only supported)
   */
  getFileIcon(documentType: string): string {
    if (documentType === 'application/pdf') {
      return '📄';
    }
    if (documentType.startsWith('text/')) {
      return '📝';
    }
    return '📎';
  }

  /**
   * Validate file before upload. Uses same allowed types as chat: PDF and text only.
   */
  validateFile(file: File): { valid: boolean; error?: string } {
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (file.size > maxSize) {
      return { valid: false, error: 'File size must be less than 10MB' };
    }

    if (!isAllowedChatFileType(file.type)) {
      return { valid: false, error: 'File type not supported. Only PDF and text files are allowed.' };
    }

    return { valid: true };
  }

  /**
   * Get documents linked to an experiment
   * For now, this filters project documents by experiment metadata
   * Future: Backend will support experiment-scoped document queries
   */
  async getExperimentDocuments(projectId: string, experimentId: string): Promise<Document[]> {
    const allDocuments = await this.getProjectDocuments(projectId);
    // Frontend filtering: Check if document metadata includes experimentId
    // For now, return all project documents since backend doesn't support experiment filtering yet
    // In the future, this will filter by experiment_document junction table
    return allDocuments.filter((doc) => {
      // TODO: When backend supports experiment_document table, filter here
      // For now, return all project documents
      return true;
    });
  }

  /**
   * Link a document to an experiment (frontend metadata only for now)
   * Future: Backend will support experiment_document junction table
   */
  async linkDocumentToExperiment(
    projectId: string,
    documentId: string,
    experimentId: string
  ): Promise<void> {
    // TODO: When backend supports experiment_document table, make API call here
    // For now, this is a no-op as documents are project-scoped
    // Frontend will track experiment associations via metadata
  }

  /**
   * Unlink a document from an experiment
   */
  async unlinkDocumentFromExperiment(
    projectId: string,
    documentId: string,
    experimentId: string
  ): Promise<void> {
    // TODO: When backend supports experiment_document table, make API call here
  }
}

export const documentService = new DocumentService();
